/**
 * Legacy primitive-box color paths → plain CSS colors (no CSS variables / theme package).
 */
const LEGACY_TOKEN_TO_CSS: Record<string, string> = {
  transparent: "transparent",
  "primary.textHigh": "hsl(222.2 84% 4.9%)",
  "primary.textLow": "hsl(215.4 16.3% 46.9%)",
  "primary.bgBase": "hsl(0 0% 100%)",
  "primary.bgSubtle": "hsl(210 40% 96.1%)",
  "primary.element": "hsl(210 40% 96.1%)",
  "primary.elementHover": "hsl(210 40% 96.1%)",
  "primary.elementActive": "hsl(210 40% 96.1%)",
  "primary.borderSubtle": "hsl(214.3 31.8% 91.4%)",
  "primary.border": "hsl(214.3 31.8% 91.4%)",
  "primary.borderHover": "hsl(222.2 84% 4.9%)",
  "primary.borderActive": "hsl(222.2 84% 4.9%)",
  "primary.accent": "hsl(222.2 47.4% 11.2%)",
  "primary.navOnAccent": "hsl(210 40% 98%)",
  "primary.accentHover": "hsl(222.2 84% 4.9%)",
  "secondary.textHigh": "hsl(222.2 47.4% 11.2%)",
  "secondary.accent": "hsl(210 40% 96.1%)",
  "secondary.accentHover": "hsl(210 40% 96.1%)",
  "secondary.border": "hsl(214.3 31.8% 91.4%)",
  "secondary.borderSubtle": "hsl(214.3 31.8% 91.4%)",
  "secondary.borderActive": "hsl(222.2 84% 4.9%)",
  "secondary.elementActive": "hsl(210 40% 96.1%)",
  "secondary.bgBase": "hsl(222.2 47.4% 11.2%)",
  "danger.textLow": "hsl(0 84.2% 60.2%)",
  "danger.accent": "hsl(0 84.2% 60.2%)",
  "danger.borderSubtle": "hsl(0 84.2% 60.2% / 0.35)",
  "warning.accent": "hsl(38 92% 50%)",
  "warning.bgBase": "hsl(48 96% 89%)",
  "form.bg": "hsl(210 40% 96.1%)",
  "form.text": "hsl(215.4 16.3% 46.9%)",
};

const SHADOWS = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  inset: "inset 0 1px 0 rgb(0 0 0 / 0.04)",
} as const;

function pick<T>(v: T | T[] | undefined): T | undefined {
  if (v === undefined) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

export function resolveColorToken(raw: unknown): string | number | undefined {
  const v = pick(raw as string | string[]);
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number") return v;
  const s = String(v);
  if (
    s === "transparent" ||
    s === "currentColor" ||
    s.startsWith("#") ||
    s.startsWith("rgb") ||
    s.startsWith("hsl") ||
    s.startsWith("var(")
  ) {
    return s;
  }
  if (!s.includes(".")) return s;
  const css = LEGACY_TOKEN_TO_CSS[s];
  if (css) return css;
  return s;
}

export const resolveThemeValue = resolveColorToken;

const SHADOW_ALIAS: Record<string, keyof typeof SHADOWS> = {
  s: "sm",
  m: "md",
  l: "lg",
  u: "inset",
};

export function getThemeShadow(key: string): string {
  const k = SHADOW_ALIAS[key] ?? (key as keyof typeof SHADOWS);
  return SHADOWS[k] ?? SHADOWS.md;
}
